/**
 * Login help — the contextual support message sent from the login screen's help button.
 *
 * Why this exists: nobody stuck on the login screen can press the in-app help button of the
 * app itself, and access problems (a rotated OTP, an expired session, a device typo) are the
 * single biggest support topic — 27 % of tablet conversations in the last 90 days. Until now
 * the login help button just opened the Intercom messenger empty, so the kitchen had to explain,
 * from a screen they cannot get past, what they were even trying. Support then plays twenty
 * questions: which device? which restaurant? what did you type?
 *
 * This composes a message that answers all of that before anyone reads it: what they are trying
 * now (device id + the OTP they typed), the raw error the app showed, and — the part that says
 * "who is writing" even when the current attempt fails — the last account that logged in
 * successfully on this tablet. That last login is persisted separately from the working
 * credentials (see LOGIN_HISTORY_KEY) precisely so it survives the auth failure that cleared
 * them.
 *
 * Pure and i18n-driven, like composeHelpMessage in helpFlow.js, so the whole message is unit
 * tested against the real catalogue in the three locales.
 */

/**
 * localStorage key holding the last SUCCESSFUL login on this tablet. Written by App.vue once the
 * restaurant is known, and — unlike `device_id` / `otp` — never cleared on an auth failure, so a
 * kitchen that can no longer log in still tells support which restaurant they are.
 */
export const LOGIN_HISTORY_KEY = 'bronze_last_login';

/** Record the last good login. Best-effort: a storage that throws must never break the login. */
export function rememberLastLogin(storage, { deviceId, restaurant, now = Date.now() } = {}) {
  if (!storage || !deviceId) return false;
  try {
    storage.setItem(LOGIN_HISTORY_KEY, JSON.stringify({ deviceId, restaurant: restaurant || null, at: now }));
    return true;
  } catch (_) {
    return false;
  }
}

/** Read the last good login, or null when there is none or it is unreadable. */
export function readLastLogin(storage) {
  if (!storage) return null;
  let raw = null;
  try {
    raw = storage.getItem(LOGIN_HISTORY_KEY);
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  try {
    const record = JSON.parse(raw);
    if (!record || typeof record !== 'object' || !record.deviceId) return null;
    return record;
  } catch (_) {
    return null;
  }
}

/**
 * The user_id the help conversation should be attributed to on Intercom, so support sees which
 * restaurant is writing. The last CONFIRMED login wins over what is being typed now: a device
 * typed on a failing attempt may be wrong, while the last good login is a fact. Falls back to the
 * typed device only on a tablet that has never logged in (first setup). Null when neither exists.
 */
export function loginHelpAttribution({ typedDeviceId, lastLogin } = {}) {
  if (lastLogin && lastLogin.deviceId) return lastLogin.deviceId;
  const typed = (typedDeviceId || '').trim();
  return typed || null;
}

// Coarse "how long ago" so support knows whether the last login was this shift or last week. No
// date-fns: a handful of thresholds keeps this pure and testable, and minute precision is noise
// here.
function relativeAge(ms, t) {
  if (typeof ms !== 'number' || ms < 0) return null;
  const min = Math.floor(ms / 60000);
  if (min < 1) return t('message.loginHelpJustNow');
  if (min < 60) return t('message.loginHelpMinutesAgo', { minutes: min });
  const hours = Math.floor(min / 60);
  if (hours < 24) return t('message.loginHelpHoursAgo', { hours });
  const days = Math.floor(hours / 24);
  return t('message.loginHelpDaysAgo', { days });
}

/**
 * The message the login help button sends to support.
 *
 * @param {object} args
 * @param {object} args.attempt   `{ deviceId, otp }` — what is typed in the form right now
 * @param {object|null} args.lastLogin  from readLastLogin: `{ deviceId, restaurant, at }`
 * @param {string} args.appVersion
 * @param {string|null} [args.error]  the raw login error the app last showed, if any
 * @param {number} [args.now]
 * @param {Function} args.t        vue-i18n translate
 */
export function composeLoginHelpMessage({ attempt = {}, lastLogin = null, appVersion, error = null, now = Date.now(), t }) {
  const device = (attempt.deviceId || '').trim();
  const otp = (attempt.otp || '').toString().trim();

  const lines = [t('message.loginHelpIntro')];

  // What they are trying now. Each field degrades to an explicit "none typed" so support never
  // wonders whether a blank means empty or dropped.
  lines.push(t('message.loginHelpAttempt', {
    device: device || t('message.loginHelpNoDevice'),
    otp: otp || t('message.loginHelpNoOtp'),
  }));

  if (error) lines.push(t('message.loginHelpError', { error: cleanError(error) }));

  // Who is writing: the last confirmed login on this tablet.
  if (lastLogin && lastLogin.deviceId) {
    const at = Number.isFinite(lastLogin.at) ? lastLogin.at : now; // at===0 is a valid timestamp, not "missing"
    const age = relativeAge(now - at, t);
    lines.push(t('message.loginHelpLastLogin', {
      device: lastLogin.deviceId,
      restaurant: lastLogin.restaurant || t('message.loginHelpUnknownRestaurant'),
      age: age || t('message.loginHelpJustNow'),
    }));
  } else {
    lines.push(t('message.loginHelpNoHistory'));
  }

  lines.push('—');
  lines.push(t('message.loginHelpTech', { version: appVersion || '?' }));
  return lines.join('\n');
}

// The app's login errors are prefixed for the console ("Could not sign-in: ..."); strip that so
// support reads the cause, not our log format. Kept verbatim otherwise — a raw error is more
// actionable here than a translated one.
function cleanError(error) {
  return String(error).replace(/^Could not sign-in:\s*/i, '').replace(/^Error during sign in:\s*/i, '').trim();
}
